import type { EditorState } from "../vim/model/editor-state";
import type { DrillGoal } from "./model/drill-goal";
import type { Verdict } from "./model/verdict";

function buffersEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((line, index) => line === b[index]);
}

function cursorsEqual(a: { line: number; col: number }, b: { line: number; col: number }): boolean {
  return a.line === b.line && a.col === b.col;
}

/**
 * 技が確定していない（＝1つの操作の途中である）状態か。
 * オペレータ待ち・カウント入力中・`f`/`t` の対象待ち（`pending`）と、`/` `?` `:` の
 * 入力途中（`commandLine`）は、まだ「打ち終えていない」状態であり判定にかけない。
 */
function isMidCommand(state: EditorState): boolean {
  return state.pending.rawKeys.length > 0 || state.commandLine !== null;
}

/**
 * この試行で打った技の並びに、要求された技がすべて含まれるか。
 * 打鍵の表記を連結した文字列に対する部分一致で照合するため、`gg` や `<C-r>` のように
 * 複数キー・複数文字からなる表記もそのまま指定できる。
 */
function usedAllRequiredCommands(
  required: readonly string[],
  executedKeys: readonly string[],
): boolean {
  const executed = executedKeys.join("");
  return required.every((command) => executed.includes(command));
}

/**
 * 演習の合否を判定する純関数（F-004）。
 * `goal` の各項目は任意であり、**定義された条件のみを判定する**（ADR-010）。
 * すべての定義済み条件を満たしていなければ `unmet`。満たしていれば、手数が上限以内かで
 * `passed` / `over-limit` を返す。
 *
 * ただし「条件を満たした瞬間」がそのまま合格になるわけではない。判定は**技が確定した
 * 状態**に対してのみ下す:
 *  - 操作の途中（オペレータ待ち・コマンドライン入力中）は常に `unmet`
 *  - `requireMode` を明示していない演習は、ノーマルモードへ戻るまで合格にしない
 *    （挿入モードで文字を打っている最中に、たまたま紙面が一致して合格するのを防ぐ）
 *  - `requiredCommands` を持つ演習は、その技を実際に打っていなければ `off-technique`
 *    （`w` を学ぶ演習に `l` の連打で辿り着くような「稽古になっていない別経路」を弾く。
 *    的には届いているため `unmet` とは区別し、師範代が声をかけられるようにする）
 *
 * `executedKeys` はこの試行で受理された打鍵の表記列（未習の打鍵は含まない）。
 * `requiredCommands` を持たない演習では判定に影響しない。
 */
export function judge(
  goal: DrillGoal,
  state: EditorState,
  used: number,
  limit: number,
  executedKeys: readonly string[] = [],
): Verdict {
  if (isMidCommand(state)) {
    return { kind: "unmet", usedKeystrokes: used, limit };
  }
  if (goal.buffer !== undefined && !buffersEqual(goal.buffer, state.buffer.lines)) {
    return { kind: "unmet", usedKeystrokes: used, limit };
  }
  if (goal.cursor !== undefined && !cursorsEqual(goal.cursor, state.cursor)) {
    return { kind: "unmet", usedKeystrokes: used, limit };
  }
  const requiredMode = goal.requireMode ?? "normal";
  if (requiredMode !== state.mode) {
    return { kind: "unmet", usedKeystrokes: used, limit };
  }
  if (
    goal.requiredCommands !== undefined &&
    !usedAllRequiredCommands(goal.requiredCommands, executedKeys)
  ) {
    // 的には届いているので、黙って落とさず「稽古になっていない」ことが分かる判定を返す。
    return { kind: "off-technique", usedKeystrokes: used, limit };
  }

  return used <= limit
    ? { kind: "passed", usedKeystrokes: used, limit }
    : { kind: "over-limit", usedKeystrokes: used, limit };
}
